import { DataGrid, Icon, TableCell, TextInput, theme } from '@ketch-com/deck'
import {
  GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO,
  RightInvocationDTO,
  RightInvocationRequestStatusDTO,
} from '@ketch-com/figurehead'
import { GridRenderCellParams, GridColDef } from '@mui/x-data-grid-premium'
import { Box, Typography } from '@mui/material'
import { formatDateTimeFromUnix } from 'utils/helpers/dateTime'
import { RoutesManager } from 'utils/routing/routesManager'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { AuditLogRightViewRequestCell } from 'pages/insights/auditLog/components/rightsTab/components/AuditLogRightViewRequestCell'
import { useMemo, useState } from 'react'
import { RightListStatusCellRendererV2 } from 'pages/orchestration/rightsQueue/listV2/components'
import { DataRendererWithTooltip } from 'pages/orchestration/rightsQueue/listV2/components/RightsQueueListTable/hooks/useRightsQueueListTableDataGridColumns'
import { useDebounce } from 'react-use'
import { NoDataEmptyState } from 'pages/insights/auditLog/components/NoDataEmptyState'

const rightInvocationRequestStatusMap: Record<string, RightInvocationRequestStatusDTO> = {
  Completed: RightInvocationRequestStatusDTO.FulfilledRightInvocationRequestStatus,
  Rejected: RightInvocationRequestStatusDTO.RejectedRightInvocationRequestStatus,
  'On track': RightInvocationRequestStatusDTO.OnTrackRightInvocationRequestStatus,
  'Due soon': RightInvocationRequestStatusDTO.DueSoonRightInvocationRequestStatus,
  Urgent: RightInvocationRequestStatusDTO.OverdueRightInvocationRequestStatus,
}

const auditLogsInvokedRightsColumns: GridColDef<GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO>[] = [
  {
    align: 'left',
    field: 'fulfillmentStatus',
    headerAlign: 'left',
    headerName: 'Status',
    width: 206,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO>) => {
      const rightInvocation: RightInvocationDTO = {
        requestStatus: row.status
          ? rightInvocationRequestStatusMap[row.status]
          : RightInvocationRequestStatusDTO.OnTrackRightInvocationRequestStatus,
        fulfilledAt: row.fulfilledAt,
        requestStatusText: row.requestStatusText,
        rightName: row.name,
        id: row.id,
      }
      return (
        <TableCell>
          <RightListStatusCellRendererV2 rightInvocation={rightInvocation} />
        </TableCell>
      )
    },
  },
  {
    align: 'left',
    field: 'request',
    headerAlign: 'left',
    headerName: 'Request',
    sortable: false,
    width: 550,
    renderCell: ({ row }: GridRenderCellParams<GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO>) => {
      const rightInvocation: RightInvocationDTO = {
        requestStatus: row.status
          ? rightInvocationRequestStatusMap[row.status]
          : RightInvocationRequestStatusDTO.OnTrackRightInvocationRequestStatus,
        fulfilledAt: row.fulfilledAt,
        requestStatusText: row.requestStatusText,
        rightName: row.name,
        id: row.id,
      }
      return <DataRendererWithTooltip rightInvocation={rightInvocation} />
    },
  },
  {
    align: 'left',
    field: 'date',
    width: 300,
    headerAlign: 'left',
    headerName: 'Date',
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO>) => {
      return (
        <TableCell sx={{ padding: '8px 0' }}>
          {!row.date ? (
            <Typography color="darkGrey.main">None</Typography>
          ) : (
            <Typography>{formatDateTimeFromUnix(row.date)}</Typography>
          )}
        </TableCell>
      )
    },
  },
  {
    align: 'right',
    field: 'actions',
    headerAlign: 'left',
    headerName: '',
    flex: 1,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<GetRightInvocationsByIdentityResponseBodyRightInvocationsItemDTO>) => {
      return (
        <AuditLogRightViewRequestCell
          route={RoutesManager.orchestration.rightsQueue.view.root.getURL({ id: row.id })}
        />
      )
    },
  },
]

export const AuditLogRightsTab: React.FC = () => {
  const { rightsData, rightsPagination, isLoading, rightIdSearch, setRightIdSearch } = useAuditLogContext()

  // Debounce right ID search
  const [localRightIdSearch, setLocalRightIdSearch] = useState(rightIdSearch)
  useDebounce(
    () => {
      setRightIdSearch(localRightIdSearch)
    },
    500,
    [localRightIdSearch],
  )

  const lastUpdated = useMemo(() => {
    return rightsData.reduce((max, right) => {
      return Math.max(max, right.date || 0)
    }, 0)
  }, [rightsData])

  return (
    <Box display="flex" flexDirection="column" width="100%" gap="8px">
      {rightsData.length ? (
        <>
          <Typography variant="h3" mt={2} mb={2}>{`${rightsPagination.totalResults || 0} Invoked Rights`}</Typography>
          <Box mb="24px">
            <TextInput
              size="small"
              value={localRightIdSearch}
              placeholder="Search for a request ID"
              onChange={e => setLocalRightIdSearch(e.target.value)}
              startIcon={<Icon name={'OMag'} iconColor={theme.palette.Black.o48} />}
              sx={{ mr: '14px', width: '230px' }}
            />
            <Typography variant="smallBody" color={theme.palette.Text.Secondary}>
              Last updated: {formatDateTimeFromUnix(lastUpdated)}
            </Typography>
          </Box>
          <DataGrid
            autosizeOnMount
            columns={auditLogsInvokedRightsColumns}
            disableChildrenSorting
            disableColumnMenu
            disableColumnPinning
            disableColumnResize
            disableColumnReorder
            getRowId={row => row.id}
            loading={isLoading || rightsPagination.isPending}
            rows={rightsData}
            {...rightsPagination.dataGridProps}
            sx={{
              height: 'auto',
            }}
          />
        </>
      ) : (
        <NoDataEmptyState title="No Requests" subtitle="We couldn't find any right requests for this identity." />
      )}
    </Box>
  )
}
